<script setup lang="tsx">
import { CirclePlus, Fold, Plus, Search } from "@element-plus/icons-vue";
import {
  Column,
  ElButton,
  RowClassNameGetter,
  SortBy,
  TableV2SortOrder,
} from "element-plus";
import { computed, defineComponent, onMounted, ref } from "vue";
import { SysOrg, SysOrgRoleName, SysOrgRoles } from "~/api";
import TableWrapper from "~/components/TableWrapper.vue";
import { authStore } from "~/stores/authStore";
import { useMyInfoStore } from "~/stores/MyInfoStore";
import { useOrgStore } from "~/stores/OrgStore";
import { apiClient } from "~/utils/apiClient";
import { getEnvConfig } from "~/services/appConfig";
import { showError } from "~/utils/handleAPIError";
import { refreshCaches } from "~/utils/miscUtils";
import { useStableAutoResizer } from "~/utils/resizeObserverFix.simple";
import EditOrg from "../components/EditOrg.vue";
const authToken = ref<string>();

const apiBase = getEnvConfig().serverUrl;

type OrgPlus = SysOrg & {
  search: string;
  numMyRoles: number;
  hasUserAdminRole: boolean;
};

const orgStore = useOrgStore();
const myInfoStore = useMyInfoStore();

const loading = ref(false);

// Prevent ResizeObserver performance issues
const { autoResizerRef } = useStableAutoResizer();

/** The list of all orgs from the server */
const orgs = ref<OrgPlus[]>([]);

/** the current query for orgs */
const orgSearchQuery = ref<string>("");

/** The org we are currently creating/editing in the dialog */
const orgBeingEdited = ref<SysOrg>();

/** 'true' if the edit/create dialog is open */
const orgDialogIsOpen = ref<boolean>(false);

/** Need open counter so that :key is different every time */
const openCounter = ref(0);

const hasViewSetupRole =
  myInfoStore.currentSysOrg?.roles.includes(SysOrgRoleName.VIEW_SETUP) ||
  myInfoStore.isSuperAdmin;

const filteredOrgs = computed(() => {
  const searchTerm = orgSearchQuery.value.toLowerCase();
  return orgs.value.filter((org) => org.search.includes(searchTerm));
});

const extendOrgs = (source: SysOrg[]): OrgPlus[] => {
  return source.map(extendOrg);
};

const extendOrg = (org: SysOrg): OrgPlus => {
  return {
    ...org,
    numMyRoles: org.loginRoleSysOrgs?.length || 0,
    hasUserAdminRole:
      org.loginRoleSysOrgs?.some(
        (role: SysOrgRoles) =>
          role.sysOrgRoleName === SysOrgRoleName.USER_ADMIN,
      ) ?? false,
    search: [
      org.name,
      org.code,
      org.email,
      org.websiteUrl,
      org.contactNumber,
      org.emailDomains,
    ]
      .join(" ")
      .toLowerCase(),
  };
};

const sortState = ref<SortBy>({ key: 0, order: TableV2SortOrder.ASC });
const lastState = ref<SortBy>({ key: 0, order: TableV2SortOrder.DESC });

const onSort = (params: SortBy) => {
  sortState.value = params;
  sortResults();
};

const sortResults = () => {
  const { key, order } = sortState.value;
  if (key) {
    const delta = order === TableV2SortOrder.ASC ? 1 : -1;
    lastState.value = { key, order };
    orgs.value.sort((a, b) => {
      return (a[key as keyof SysOrg] ?? "") > (b[key as keyof SysOrg] ?? "")
        ? delta
        : -delta;
    });
  } else {
    orgs.value.sort((a, b) => {
      const nameA = a.name || "";
      const nameB = b.name || "";
      return nameA.localeCompare(nameB);
    });
  }
};

const retrieveOrgs = async () => {
  const rawList = await apiClient.sysOrg.getOrgs();
  orgs.value = extendOrgs(rawList);
};

const handleCreateOrg = () => {
  orgBeingEdited.value = undefined;
  openCounter.value++;
  orgDialogIsOpen.value = true;
};

const handleAddOrg = (org: SysOrg) => {
  orgs.value.push(extendOrg(org));
};

const joinOrg = async (org: SysOrg) => {
  if (!org || !org.guid) {
    return;
  }
  await apiClient.sysOrg
    .joinSysOrg(org.guid)
    .then(async () => {
      await refreshCaches();
      location.reload();
    })
    .catch(showError);
};

const handleEditOrg = (toEdit: SysOrg) => {
  orgBeingEdited.value = toEdit;
  openCounter.value++;
  orgDialogIsOpen.value = true;
};

const handleUpdateOrg = (org: SysOrg) => {
  const index = orgs.value.findIndex(
    (d) => d.code === orgBeingEdited.value?.code,
  );

  // replace the org with the updated one
  if (index !== undefined && index !== -1) {
    orgs.value.splice(index, 1, extendOrg(org));
  }
  // if the index wasn't found that means it's a new org
  else {
    showError(
      "Developer Error: Tried to update an org that doesn't exist",
      org,
    );
  }
  orgStore.load(true);
};

const getLogoImageUrl = async (
  fileGuid: string | undefined | null,
): Promise<string | null> => {
  if (!authToken.value || !fileGuid) {
    return null;
  }

  try {
    const response = await fetch(`${apiBase}/api/files/thumbnail/${fileGuid}`, {
      headers: {
        Authorization: `Bearer ${authToken.value}`,
      },
    });

    if (!response.ok) {
      return null;
    }

    const blob = await response.blob();
    return URL.createObjectURL(blob);
  } catch (err) {
    console.log(`Can't get logo for fileGuid "${fileGuid}":`, err);
    return null;
  }
};

const LogoImage = defineComponent({
  props: {
    logoFileGuid: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const logoUrl = ref<string | null>(null);
    const loading = ref(true);

    onMounted(async () => {
      try {
        logoUrl.value = await getLogoImageUrl(props.logoFileGuid);
      } finally {
        loading.value = false;
      }
    });

    return () => {
      if (loading.value) {
        return <span class="loading-logo">Loading...</span>;
      }

      if (logoUrl.value) {
        return (
          <img
            src={logoUrl.value}
            alt="Organization Logo"
            style="max-height: 50px; max-width: 70px; object-fit: contain;"
          />
        );
      }

      return <span class="no-logo">—</span>;
    };
  },
});

const tempColumns: Column<SysOrg>[] = [
  {
    title: "Name",
    key: "name",
    dataKey: "name",
    width: 300,

    cellRenderer: (row: any) => {
      if (hasViewSetupRole) {
        return (
          <ElButton
            text
            onClick={() => handleEditOrg(row.rowData)}
            icon={Fold}
            type="primary"
            style="font-size: 15px;"
          >
            {row.rowData.name}
          </ElButton>
        );
      } else {
        return <span>{row.rowData.name}</span>;
      }
    },
  },
  {
    title: "Code",
    key: "code",
    dataKey: "code",
    width: 100,
  },
  {
    title: "Logo",
    key: "logoFileGuid",
    dataKey: "logoFileGuid",
    width: 80,
    align: "center",
    cellRenderer: (row: any) => {
      const logoFileGuid = row.rowData.logoFileGuid;
      if (logoFileGuid) {
        return <LogoImage logoFileGuid={logoFileGuid} />;
      } else {
        return <span class="no-logo">—</span>;
      }
    },
  },
  {
    title: "My Roles",
    key: "numMyRoles",
    dataKey: "numMyRoles",
    width: 100,
    align: "center",
    cellRenderer: (row: any) => {
      if (row.rowData.hasUserAdminRole) {
        return <span>{row.rowData.numMyRoles}</span>;
      } else {
        return (
          <div
            class="myRoles"
            title={"Give me 'User Admin' role in this organization"}
          >
            <span>{row.rowData.numMyRoles}</span>
            <ElButton
              type="info"
              circle
              size="small"
              onClick={() => joinOrg(row.rowData)}
              icon={Plus}
            ></ElButton>
          </div>
        );
      }
    },
  },
  {
    title: "Contact Number",
    key: "contactNumber",
    dataKey: "contactNumber",
    width: 200,
  },
  {
    title: "Email",
    key: "email",
    dataKey: "email",
    width: 250,
  },
  {
    title: "Website URL",
    key: "websiteUrl",
    dataKey: "websiteUrl",
    width: 250,
  },
  {
    title: "Email Domains",
    key: "emailDomains",
    dataKey: "emailDomains",
    width: 250,
    cellRenderer: (row: any) => {
      return (
        <div>
          {row.rowData.emailDomains?.split(",").map((domain: string) => (
            <div>{domain.trim()}</div>
          ))}
        </div>
      );
    },
  },
  // {
  //   title: "Action",
  //   key: "",
  //   dataKey: "",
  //   width: 100,
  //   cellRenderer: (row: any) => {
  //     if (myInfoStore.isSuperAdmin) {
  //       return (
  //         <ElButton
  //           text
  //           onClick={() => handleDeleteOrg(row.rowData.guid)}
  //           icon={Delete}
  //           type="primary"
  //           style="font-size: 15px; color: var(--el-color-danger)"
  //         >
  //           Delete
  //         </ElButton>
  //       );
  //     } else {
  //       return <span></span>;
  //     }
  //   },
  // },
];

const columns = computed(() =>
  tempColumns
    .filter((col) => col.key !== "numMyRoles" || myInfoStore.isSuperAdmin)
    .map((col) => {
      return {
        ...col,
        sortable: true,
      };
    }),
);

const rowClass = ({ rowData }: Parameters<RowClassNameGetter<SysOrg>>[0]) => {
  return myInfoStore.currentSysOrg?.guid === rowData.guid ? "current-org" : "";
};

const refreshOrgs = async () => {
  loading.value = true;
  await Promise.all([retrieveOrgs(), myInfoStore.load()]);
  loading.value = false;
};

onMounted(async () => {
  authToken.value = (await authStore.getAccessTokenAsync()) ?? undefined;
  await Promise.all([refreshOrgs(), myInfoStore.load()]);
});
</script>

<template>
  <div class="OrganizationList">
    <div class="flexRow">
      <span class="showingInTable">
        Showing {{ filteredOrgs.length }} of
        {{ orgs.length }}
      </span>
      <RefreshButton @click="refreshOrgs" />
      <el-input
        v-if="orgs.length > 5"
        v-model="orgSearchQuery"
        clearable
        placeholder="Search"
        :prefix-icon="Search"
      />
      <el-button
        v-if="myInfoStore.isSuperAdmin"
        class="addButton"
        :icon="CirclePlus"
        title="Add New Organization"
        @click="handleCreateOrg"
      >
        New Organization
      </el-button>
    </div>
    <!-- TABLE FOR Orgs -->
    <ElAutoResizer ref="autoResizerRef" v-loading="loading">
      <template #default="{ height, width }">
        <TableWrapper
          v-if="!loading"
          ref="mainTableRef"
          :columns="columns"
          :data="filteredOrgs"
          row-key="guid"
          :height="height"
          :width="width"
          :sort-state="sortState"
          :row-class="rowClass"
          @column-sort="onSort"
        >
        </TableWrapper>
      </template>
    </ElAutoResizer>

    <EditOrg
      v-if="orgBeingEdited || orgDialogIsOpen"
      :key="openCounter"
      v-model="orgDialogIsOpen"
      :org="orgBeingEdited"
      @update:org="handleUpdateOrg"
      @add:org="handleAddOrg"
    />
  </div>
</template>

<style lang="less">
.OrganizationList {
  display: grid;
  grid-template-rows: 60px auto;
  height: 100%;

  .icon {
    cursor: pointer;
    margin-left: 0.5em;
    color: var(--el-color-primary);
    font-size: 1.2em;
    vertical-align: text-bottom;
  }

  .ep-table .recently-changed {
    --ep-table-tr-bg-color: var(--ep-color-primary-light-7) !important;
  }
  .ep-table {
    // height: 75vh;
    --ep-table-border-color: #c1c2c3;
  }

  .current-org {
    box-shadow: inset 3px 0px 0px var(--el-color-primary);
  }

  .filters {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 1em;
    margin: 1em;

    .alignRight {
      display: flex;
      align-items: center;
      gap: 1em;
    }
  }

  .flexRow {
    align-items: center;
  }

  .myRoles {
    display: flex;
    gap: 0.5em;
    align-items: center;
  }

  .no-logo {
    color: var(--el-color-info);
    font-size: 0.9em;
  }

  .loading-logo {
    color: var(--el-color-info);
    font-size: 0.8em;
    font-style: italic;
  }
}
</style>
